"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  emitModuleInteraction,
  type ModuleInteractionTargetKind,
} from "@/lib/module-interaction";
import { useLegendClickSync } from "@/components/charts/use-legend-click-sync";

/**
 * Which surface a selection was made on. Highlighting answers to both, so a
 * legend row and its chart element still light each other up; only a legend
 * gesture may reduce or dim the visible set.
 */
export type HoverSelectOrigin = "legend" | "element";

/** One selection state inside a `SelectionGroup`. */
export interface SelectionGroupMember {
  clear: () => void;
}

/**
 * The surfaces of one module that express the *same* selection. A module that
 * keeps its element picks and its legend picks in separate state has two
 * selections that never cancel each other: both stand at once, the derived
 * values read whichever they happen to name, and releasing one uncovers the
 * other. Joining them makes a new pick on any surface drop the pick on every
 * other, so at most one of them is ever set.
 *
 * Members are the independent *states*, not the surfaces: a legend that already
 * backs its element hook through `control` is a single state and must join only
 * once, otherwise the join would clear the very value the select is toggling.
 */
export interface SelectionGroup {
  register: (member: SelectionGroupMember) => () => void;
  clearOthers: (member: SelectionGroupMember) => void;
}

export function useSelectionGroup(): SelectionGroup {
  const membersRef = useRef<Set<SelectionGroupMember>>(new Set());
  return useMemo<SelectionGroup>(
    () => ({
      register: (member) => {
        membersRef.current.add(member);
        return () => {
          membersRef.current.delete(member);
        };
      },
      clearOthers: (member) => {
        membersRef.current.forEach((other) => {
          if (other !== member) other.clear();
        });
      },
    }),
    [],
  );
}

/**
 * Holds one stable member object for the lifetime of the hook instance while
 * letting its `clear` follow the latest setter, which a `control` rebuilds on
 * every render.
 */
function useSelectionGroupMember(
  group: SelectionGroup | undefined,
  clear: () => void,
): SelectionGroupMember {
  const clearRef = useRef(clear);
  clearRef.current = clear;
  const memberRef = useRef<SelectionGroupMember | null>(null);
  if (memberRef.current === null) {
    memberRef.current = {
      clear: () => {
        clearRef.current();
      },
    };
  }
  const member = memberRef.current;

  useEffect(() => {
    if (!group) return;
    return group.register(member);
  }, [group, member]);

  return member;
}

export interface HoverSelectSource {
  kind?: ModuleInteractionTargetKind;
  /**
   * Overrides what `kind` implies. A control that picks a category rather than
   * a datum — a share-of-total pie next to the legend it mirrors — counts as
   * the legend while still reporting its own kind to analytics.
   */
  origin?: HoverSelectOrigin;
  id?: string | number | null;
  index?: number | null;
  group?: string | number | null;
  /** Element the analytics event is dispatched from; without one none is sent. */
  element?: Element | null;
}

/** The select half, when something else already owns it — a legend, say. */
export interface HoverSelectControl<K> {
  selected: K | null;
  select: (key: K, source?: HoverSelectSource) => void;
  setSelected: (key: K | null) => void;
}

export interface HoverSelect<K> {
  hovered: K | null;
  selected: K | null;
  /**
   * What everything that reacts to either reads: the tooltip, the dimming and
   * the highlight. A live hover wins over a standing selection.
   */
  effective: K | null;
  /**
   * False while `selected` is still the value the module started with. A
   * seeded selection is presentation, not an interaction, so a tooltip that
   * follows the selection must not open on mount.
   */
  selectedByUser: boolean;
  /**
   * Where the standing `selected` was made. A select naming any target kind
   * other than the legend item counts as the element's unless its source says
   * otherwise; the legend either says `"legend-item"` or passes no source at
   * all. Null until the first select.
   * `setSelected` is a programmatic write and leaves it alone, so it can never
   * turn a seeded or cleared selection into a filtering one.
   */
  selectedOrigin: HoverSelectOrigin | null;
  setHovered: (key: K | null) => void;
  /** Toggles: selecting what is already selected clears it. */
  select: (key: K, source?: HoverSelectSource) => void;
  setSelected: (key: K | null) => void;
}

export interface UseHoverSelectOptions<K> {
  initialSelected?: K | null;
  control?: HoverSelectControl<K>;
  /**
   * How the toggle recognises "already selected". Keys that are objects need
   * this: a freshly built `{ tid, sidx }` never equals the stored one.
   */
  eq?: (a: K, b: K) => boolean;
  /**
   * The other selection states this one is an expression of. Every `select`
   * clears them first, so element and legend picks can never stand together.
   */
  group?: SelectionGroup;
}

export function useHoverSelect<K>(
  options?: UseHoverSelectOptions<K>,
): HoverSelect<K> {
  const control = options?.control;
  const [hovered, setHoveredState] = useState<K | null>(null);
  const [ownSelected, setOwnSelectedState] = useState<K | null>(
    options?.initialSelected ?? null,
  );
  const [selectedByUser, setSelectedByUser] = useState(false);
  const [selectedOrigin, setSelectedOrigin] = useState<HoverSelectOrigin | null>(
    null,
  );
  // Mirrors the state so the toggle can derive its next value without a state
  // updater, which React would run twice in dev and double-emit.
  const ownSelectedRef = useRef<K | null>(options?.initialSelected ?? null);

  const setOwnSelected = useCallback((key: K | null) => {
    ownSelectedRef.current = key;
    setOwnSelectedState(key);
  }, []);

  const eqRef = useRef(options?.eq);
  eqRef.current = options?.eq;

  const ownSelect = useCallback(
    (key: K, source?: HoverSelectSource) => {
      const current = ownSelectedRef.current;
      const same =
        current !== null && (eqRef.current?.(current, key) ?? current === key);
      const next = same ? null : key;
      setOwnSelected(next);
      emitModuleInteraction(
        source?.element,
        next === null ? "deselect" : "select",
        {
          kind: source?.kind ?? "node",
          id: source?.id ?? null,
          index: source?.index ?? null,
          ...(source?.group === undefined ? {} : { group: source.group }),
        },
      );
    },
    [setOwnSelected],
  );

  const selected = control ? control.selected : ownSelected;
  const setSelected = control ? control.setSelected : setOwnSelected;

  const group = options?.group;
  const member = useSelectionGroupMember(group, () => setSelected(null));

  const select = (key: K, source?: HoverSelectSource) => {
    // Before this one's own toggle, so the toggle still compares against this
    // member's untouched value.
    group?.clearOthers(member);
    setSelectedByUser(true);
    setSelectedOrigin(
      source?.origin ??
        (source?.kind === undefined || source.kind === "legend-item"
          ? "legend"
          : "element"),
    );
    if (control) control.select(key, source);
    else ownSelect(key, source);
  };

  return {
    hovered,
    selected,
    effective: hovered ?? selected,
    selectedByUser,
    selectedOrigin,
    setHovered: setHoveredState,
    select,
    setSelected,
  };
}

export interface LegendHoverSelect extends HoverSelect<number> {
  setLabelRef: (index: number) => (el: HTMLElement | null) => void;
  /**
   * The only value a filter may read: the live legend hover, or a standing
   * selection made at the legend. A selection forwarded here from a chart
   * element still highlights through `selected`, but must not reduce or dim
   * the set — so it is absent from this one.
   */
  filter: number | null;
}

/**
 * The legend index is the selection. `useLegendClickSync` keeps the toggle,
 * the analytics emit and the scroll-into-view; this only adds the hover half
 * and the shared `effective`.
 */
export interface UseLegendHoverSelectOptions {
  /** See `UseHoverSelectOptions.group`. */
  group?: SelectionGroup;
}

export function useLegendHoverSelect(
  options?: UseLegendHoverSelectOptions,
): LegendHoverSelect {
  const { activeIndex, setActiveIndex, handleSelect, setLabelRef } =
    useLegendClickSync();

  const base = useHoverSelect<number>({
    group: options?.group,
    control: {
      selected: activeIndex,
      select: handleSelect,
      setSelected: setActiveIndex,
    },
  });

  return {
    ...base,
    setLabelRef,
    filter:
      base.hovered ?? (base.selectedOrigin === "legend" ? base.selected : null),
  };
}
